import type { Theme } from '@mui/material/styles';
import type { SxStyles } from '@/ui/style/types';
import { visuallyHidden } from '@/ui/style/default';

/**
 * Gets the sx classes for the details components.
 *
 * @param theme - The theme object
 * @returns The sx classes object
 */
export const getSxClasses = (theme: Theme): SxStyles => ({
  detailsContainer: {
    background: theme.palette.geoViewColor.bgColor.main,
    paddingBottom: '1rem',
  },
  detailsInstructionsTitle: {
    fontSize: theme.palette.geoViewFontSize.lg,
    fontWeight: '600',
    lineHeight: '1.5em',
  },
  detailsInstructionsBody: {
    fontSize: theme.palette.geoViewFontSize.default,
  },
  panelHeaders: {
    fontSize: theme.palette.geoViewFontSize.lg,
    fontWeight: '600',
  },
  rightPanelContainer: {
    color: theme.palette.geoViewColor.textColor.main,
  },
  rightPanelBtnHolder: {
    padding: '10px 16px',
    boxShadow: `0px 12px 9px -13px ${theme.palette.geoViewColor.bgColor.dark[200]}`,
  },
  featureInfoListContainer: {
    padding: '0 16px 16px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  featureDetailListContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  featureInfoRow: {
    th: {
      fontWeight: 'bold',
    },
    '& td, & th': {
      verticalAlign: 'top',
      width: '50%',
      padding: '5px',
      borderBottom: 'none',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.geoViewColor.bgColor.darken(0.1),
      color: theme.palette.geoViewColor.bgColor.darken(0.9),
    },
  },
  featureInfoItemValue: {
    marginRight: 0,
    overflowX: 'auto',
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    ' table': {
      border: '1px solid',
      width: '100%',
      borderCollapse: 'collapse',
    },
    ' th, td': {
      border: '1px solid',
      textAlign: 'center',
      padding: '5px',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    },
  },
  featureInfoItemImage: {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  boxContainerFeatureInfo: {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    fontSize: theme.palette.geoViewFontSize.default,
    lineHeight: '19px',
    '& .MuiTable-root': {
      tableLayout: 'fixed',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
  },
  flexBoxAlignCenter: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  featureDetailModal: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        minWidth: '40rem',
      },
    },
  },
  layoutSwitch: {
    alignItems: 'center',
    display: 'flex',
    gap: '10px',
    justifyContent: 'space-between',
    marginBottom: '10px',
    width: '100%',
  },
  imageButton: {
    background: 'transparent',
    '&:focus-visible': {
      border: '2px solid currentColor',
    },
  },
  coordinateInfoContainer: {
    backgroundColor: theme.palette.geoViewColor.bgColor.light[600],
    padding: '16px',
  },
  coordinateInfoTitle: {
    mb: 10,
  },
  coordinateInfoSection: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  coordinateInfoSectionTitle: {
    fontWeight: 'bold',
  },
  visuallyHidden,
});
